:host {
  /**
   * @prop --background: Background of the item
   * @prop --background-activated: Background of the item when pressed. Note: setting this will interfere with the Material Design ripple.
   * @prop --background-activated-opacity: Opacity of the item background when pressed
   * @prop --background-focused: Background of the item when focused with the tab key
   * @prop --background-focused-opacity: Opacity of the item background when focused with the tab key
   * @prop --background-hover: Background of the item on hover
   * @prop --background-hover-opacity: Opacity of the background of the item on hover
   *
   * @prop --border-color: Color of the item border
   * @prop --border-radius: Radius of the item border
   * @prop --border-style: Style of the item border
   * @prop --border-width: Width of the item border
   *
   * @prop --color: Color of the item
   * @prop --color-activated: Color of the item when pressed
   * @prop --color-focused: Color of the item when focused with the tab key
   * @prop --color-hover: Color of the item on hover
   *
   * @prop --detail-icon-color: Color of the item detail icon
   * @prop --detail-icon-opacity: Opacity of the item detail icon
   * @prop --detail-icon-font-size: Font size of the item detail icon
   *
   * @prop --inner-border-width: Width of the item inner border
   * @prop --inner-box-shadow: Box shadow of the item inner
   * @prop --inner-padding-top: Top padding of the item inner
   * @prop --inner-padding-end: Right padding if direction is left-to-right, and left padding if direction is right-to-left of the item inner
   * @prop --inner-padding-bottom: Bottom padding of the item inner
   * @prop --inner-padding-start: Left padding if direction is left-to-right, and right padding if direction is right-to-left of the item inner
   *
   * @prop --min-height: Minimum height of the item
   *
   * @prop --padding-bottom: Bottom padding of the item
   * @prop --padding-end: Right padding if direction is left-to-right, and left padding if direction is right-to-left of the item
   * @prop --padding-start: Left padding if direction is left-to-right, and right padding if direction is right-to-left of the item
   * @prop --padding-top: Top padding of the item
   *
   * @prop --transition: Transition of the item
   *
   * @prop --ripple-color: Color of the item ripple effect
   *
   * @prop --highlight-height: The height of the highlight on the item
   * @prop --highlight-color-focused: The color of the highlight on the item when focused
   * @prop --highlight-color-valid: The color of the highlight on the item when valid
   * @prop --highlight-color-invalid: The color of the highlight on the item when invalid
   */
  --border-radius: 0px;
  --border-width: 0px;
  --border-style: solid;
  --padding-top: 0px;
  --padding-bottom: 0px;
  --padding-end: 0px;
  --padding-start: 0px;
  --inner-border-width: 0px;
  --inner-padding-top: 0px;
  --inner-padding-bottom: 0px;
  --inner-padding-start: 0px;
  --inner-padding-end: 0px;
  --inner-box-shadow: none;
  --show-full-highlight: 0;
  --show-inset-highlight: 0;
  --detail-icon-color: initial;
  --detail-icon-font-size: 20px;
  --detail-icon-opacity: 0.25;
  --color-activated: var(--color);
  --color-focused: var(--color);
  --color-hover: var(--color);
  --ripple-color: currentColor;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  display: block;
  position: relative;
  align-items: center;
  justify-content: space-between;
  outline: none;
  color: var(--color);
  font-family: var(--ion-font-family, inherit);
  text-align: initial;
  text-decoration: none;
  overflow: hidden;
  box-sizing: border-box;
}

:host(.ion-color:not(.item-fill-solid):not(.item-fill-outline)) .item-native {
  background: var(--ion-color-base);
  color: var(--ion-color-contrast);
}

:host(.ion-color:not(.item-fill-solid):not(.item-fill-outline)) .item-native,
:host(.ion-color:not(.item-fill-solid):not(.item-fill-outline)) .item-inner {
  border-color: var(--ion-color-shade);
}

:host(.ion-activated) .item-native {
  color: var(--color-activated);
}
:host(.ion-activated) .item-native::after {
  background: var(--background-activated);
  opacity: var(--background-activated-opacity);
}

:host(.ion-color.ion-activated) .item-native {
  color: var(--ion-color-contrast);
}

:host(.ion-focused) .item-native {
  color: var(--color-focused);
}
:host(.ion-focused) .item-native::after {
  background: var(--background-focused);
  opacity: var(--background-focused-opacity);
}

:host(.ion-color.ion-focused) .item-native {
  color: var(--ion-color-contrast);
}
:host(.ion-color.ion-focused) .item-native::after {
  background: var(--ion-color-contrast);
}

@media (any-hover: hover) {
  :host(.ion-activatable:not(.ion-focused):hover) .item-native {
    color: var(--color-hover);
  }
  :host(.ion-activatable:not(.ion-focused):hover) .item-native::after {
    background: var(--background-hover);
    opacity: var(--background-hover-opacity);
  }
  :host(.ion-color.ion-activatable:not(.ion-focused):hover) .item-native {
    color: var(--ion-color-contrast);
  }
  :host(.ion-color.ion-activatable:not(.ion-focused):hover) .item-native::after {
    background: var(--ion-color-contrast);
  }
}
:host(.item-interactive-disabled:not(.item-multiple-inputs)) {
  cursor: default;
  pointer-events: none;
}

:host(.item-disabled) {
  cursor: default;
  opacity: 0.3;
  pointer-events: none;
}

.item-native {
  border-radius: var(--border-radius);
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  -webkit-padding-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  padding-inline-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  -webkit-padding-end: var(--padding-end);
  padding-inline-end: var(--padding-end);
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-decoration: inherit;
  text-indent: inherit;
  text-overflow: inherit;
  text-transform: inherit;
  text-align: inherit;
  white-space: inherit;
  color: inherit;
  display: flex;
  position: relative;
  align-items: inherit;
  justify-content: inherit;
  width: 100%;
  min-height: var(--min-height);
  transition: var(--transition);
  border-width: var(--border-width);
  border-style: var(--border-style);
  border-color: var(--border-color);
  outline: none;
  background: var(--background);
  overflow: inherit;
  box-sizing: border-box;
  z-index: 1;
}

.item-native::-moz-focus-inner {
  border: 0;
}

.item-native::after {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  position: absolute;
  content: "";
  opacity: 0;
  transition: var(--transition);
  z-index: -1;
}

button, a {
  cursor: pointer;
  user-select: none;
  -webkit-user-drag: none;
}

.item-inner {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  -webkit-padding-start: var(--inner-padding-start);
  padding-inline-start: var(--inner-padding-start);
  -webkit-padding-end: calc(var(--ion-safe-area-right, 0px) + var(--inner-padding-end));
  padding-inline-end: calc(var(--ion-safe-area-right, 0px) + var(--inner-padding-end));
  padding-top: var(--inner-padding-top);
  padding-bottom: var(--inner-padding-bottom);
  display: flex;
  position: relative;
  flex: 1;
  flex-direction: inherit;
  align-items: inherit;
  align-self: stretch;
  min-height: inherit;
  border-width: var(--inner-border-width);
  border-style: var(--border-style);
  border-color: var(--border-color);
  box-shadow: var(--inner-box-shadow);
  overflow: inherit;
  box-sizing: border-box;
}

.item-bottom {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  -webkit-padding-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  padding-inline-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  -webkit-padding-end: var(--inner-padding-end);
  padding-inline-end: var(--inner-padding-end);
  padding-top: 0;
  padding-bottom: 0;
  display: flex;
  justify-content: space-between;
}

.item-detail-icon {
  /**
   * IonItem applies its own end padding. However,
   * all IonIcons have the same bounding box meaning
   * there may be white space for narrow icons. As
   * a result, the white space gives the appearance
   * that there is too much end margin. We move the default
   * chevron icon over to account for this extra whitespace.
   */
  -webkit-margin-start: calc(var(--inner-padding-end) / 2);
  margin-inline-start: calc(var(--inner-padding-end) / 2);
  -webkit-margin-end: -6px;
  margin-inline-end: -6px;
  color: var(--detail-icon-color);
  font-size: var(--detail-icon-font-size);
  opacity: var(--detail-icon-opacity);
}

::slotted(ion-icon) {
  font-size: 1.6em;
}

::slotted(ion-button) {
  --margin-top: 0;
  --margin-bottom: 0;
  --margin-start: 0;
  --margin-end: 0;
  z-index: 1;
}

::slotted(ion-label:not([slot=end])) {
  flex: 1;
}

:host(.item-input) {
  align-items: center;
}

.input-wrapper {
  display: flex;
  flex: 1;
  flex-direction: inherit;
  align-items: inherit;
  align-self: stretch;
  text-overflow: ellipsis;
  overflow: inherit;
  box-sizing: border-box;
}

:host(.item-label-stacked),
:host(.item-label-floating) {
  align-items: start;
}

:host(.item-label-stacked) .input-wrapper,
:host(.item-label-floating) .input-wrapper {
  flex: 1;
  flex-direction: column;
}

.item-highlight,
.item-inner-highlight {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  border-radius: inherit;
  position: absolute;
  width: 100%;
  height: 100%;
  transform: scaleX(0);
  transition: transform 200ms, border-bottom-width 200ms;
  z-index: 2;
  box-sizing: border-box;
  pointer-events: none;
}

:host(.item-interactive.ion-focused),
:host(.item-interactive.item-has-focus),
:host(.item-interactive.ion-touched.ion-invalid) {
  --full-highlight-height: calc(var(--highlight-height) * var(--show-full-highlight));
  --inset-highlight-height: calc(var(--highlight-height) * var(--show-inset-highlight));
}

:host(.ion-focused) .item-highlight,
:host(.ion-focused) .item-inner-highlight,
:host(.item-has-focus) .item-highlight,
:host(.item-has-focus) .item-inner-highlight {
  transform: scaleX(1);
  border-style: var(--border-style);
  border-color: var(--highlight-background);
}

:host(.ion-focused) .item-highlight,
:host(.item-has-focus) .item-highlight {
  border-width: var(--full-highlight-height);
  opacity: var(--show-full-highlight);
}

:host(.ion-focused) .item-inner-highlight,
:host(.item-has-focus) .item-inner-highlight {
  border-bottom-width: var(--inset-highlight-height);
  opacity: var(--show-inset-highlight);
}

:host(.ion-focused.item-fill-solid) .item-highlight,
:host(.item-has-focus.item-fill-solid) .item-highlight {
  border-width: calc(var(--full-highlight-height) - 1px);
}

:host(.ion-focused) .item-inner-highlight,
:host(.ion-focused:not(.item-fill-outline)) .item-highlight,
:host(.item-has-focus) .item-inner-highlight,
:host(.item-has-focus:not(.item-fill-outline)) .item-highlight {
  border-top: none;
  border-right: none;
  border-left: none;
}

:host(.item-interactive.ion-focused),
:host(.item-interactive.item-has-focus) {
  --highlight-background: var(--highlight-color-focused);
}

:host(.item-interactive.ion-valid) {
  --highlight-background: var(--highlight-color-valid);
}

:host(.item-interactive.ion-invalid) {
  --highlight-background: var(--highlight-color-invalid);
}

:host(.item-interactive.ion-invalid) ::slotted([slot=helper]) {
  display: none;
}

::slotted([slot=error]) {
  display: none;
  color: var(--highlight-color-invalid);
}

:host(.item-interactive.ion-invalid) ::slotted([slot=error]) {
  display: block;
}

:host(:not(.item-label)) ::slotted(ion-select.legacy-select) {
  --padding-start: 0;
  max-width: none;
}

:host(.item-label-stacked) ::slotted(ion-select.legacy-select),
:host(.item-label-floating) ::slotted(ion-select.legacy-select) {
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 0;
  align-self: stretch;
  width: 100%;
  max-width: 100%;
}

:host(:not(.item-label)) ::slotted(ion-datetime) {
  --padding-start: 0;
}

:host(.item-label-stacked) ::slotted(ion-datetime),
:host(.item-label-floating) ::slotted(ion-datetime) {
  --padding-start: 0;
  width: 100%;
}

:host(.item-multiple-inputs) ::slotted(ion-checkbox),
:host(.item-multiple-inputs) ::slotted(ion-datetime),
:host(.item-multiple-inputs) ::slotted(ion-radio),
:host(.item-multiple-inputs) ::slotted(ion-select.legacy-select) {
  position: relative;
}

:host(.item-textarea) {
  align-items: stretch;
}

::slotted(ion-reorder[slot]) {
  margin-top: 0;
  margin-bottom: 0;
}

ion-ripple-effect {
  color: var(--ripple-color);
}

:host(.item-fill-solid) ::slotted([slot=start]),
:host(.item-fill-solid) ::slotted([slot=end]),
:host(.item-fill-outline) ::slotted([slot=start]),
:host(.item-fill-outline) ::slotted([slot=end]) {
  align-self: center;
}

::slotted([slot=helper]),
::slotted([slot=error]),
.item-counter {
  padding-top: 5px;
  font-size: 12px;
  z-index: 1;
}

.item-counter {
  -webkit-margin-start: auto;
  margin-inline-start: auto;
  color: var(--ion-color-step-550, #737373);
  white-space: nowrap;
  padding-inline-start: 16px;
}

@media (prefers-reduced-motion: reduce) {
  .item-highlight,
  .item-inner-highlight {
    transition: none;
  }
}
:host {
  --min-height: 44px;
  --transition: background-color 200ms linear, opacity 200ms linear;
  --padding-start: 16px;
  --inner-padding-end: 16px;
  --inner-border-width: 0px 0px 0.55px 0px;
  --background: var(--ion-item-background, var(--ion-background-color, #fff));
  /**
   * The active/focus states need
   * to be the opposite of the background color
   * so there is enough contrast. The background
   * defaults to var(--ion-background-color), so we
   * default to var(--ion-text-color) which will give us
   * the opposite color.
   * i.e. if var(--ion-background-color) is #000
   * then var(--ion-text-color) will be #fff.
   */
  --background-activated: var(--ion-text-color, #000);
  --background-focused: var(--ion-text-color, #000);
  --background-hover: currentColor;
  --background-activated-opacity: .12;
  --background-focused-opacity: .15;
  --background-hover-opacity: .04;
  --border-color: var(--ion-item-border-color, var(--ion-border-color, var(--ion-color-step-250, #c8c7cc)));
  --color: var(--ion-item-color, var(--ion-text-color, #000));
  --highlight-height: 0px;
  --highlight-color-focused: var(--ion-color-primary, #3880ff);
  --highlight-color-valid: var(--ion-color-success, #2dd36f);
  --highlight-color-invalid: var(--ion-color-danger, #eb445a);
  --bottom-padding-start: 0px;
  font-size: 16px;
}

:host(.ion-activated) {
  --transition: none;
}

:host(.ion-color.ion-focused) .item-native::after {
  background: #000;
  opacity: 0.15;
}

:host(.ion-color.ion-activated) .item-native::after {
  background: #000;
  opacity: 0.12;
}

:host(.item-interactive) {
  --show-full-highlight: 0;
  --show-inset-highlight: 1;
}

:host(.item-lines-full) {
  --border-width: 0px 0px 0.55px 0px;
  --show-full-highlight: 1;
  --show-inset-highlight: 0;
}

:host(.item-lines-inset) {
  --inner-border-width: 0px 0px 0.55px 0px;
  --show-full-highlight: 0;
  --show-inset-highlight: 1;
}

:host(.item-lines-inset),
:host(.item-lines-none) {
  --border-width: 0px;
  --show-full-highlight: 0;
}

:host(.item-lines-full),
:host(.item-lines-none) {
  --inner-border-width: 0px;
  --show-inset-highlight: 0;
}

.item-highlight,
.item-inner-highlight {
  transition: none;
}

:host(.item-has-focus) .item-inner-highlight,
:host(.item-has-focus) .item-highlight {
  border-top: none;
  border-right: none;
  border-left: none;
}

::slotted([slot=start]) {
  -webkit-margin-start: 0;
  margin-inline-start: 0;
  -webkit-margin-end: 16px;
  margin-inline-end: 16px;
  margin-top: 2px;
  margin-bottom: 2px;
}

::slotted(ion-icon[slot=start]),
::slotted(ion-icon[slot=end]) {
  margin-top: 7px;
  margin-bottom: 7px;
}

::slotted(ion-toggle[slot=start]),
::slotted(ion-toggle[slot=end]) {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
}

:host(.item-label-stacked) ::slotted([slot=end]),
:host(.item-label-floating) ::slotted([slot=end]) {
  margin-top: 7px;
  margin-bottom: 7px;
}

::slotted(.button-small) {
  --padding-top: 1px;
  --padding-bottom: 1px;
  --padding-start: .5em;
  --padding-end: .5em;
  min-height: 24px;
  font-size: 13px;
}

::slotted(ion-avatar) {
  width: 36px;
  height: 36px;
}

::slotted(ion-thumbnail) {
  --size: 56px;
}

::slotted(ion-avatar[slot=end]),
::slotted(ion-thumbnail[slot=end]) {
  -webkit-margin-start: 8px;
  margin-inline-start: 8px;
  -webkit-margin-end: 8px;
  margin-inline-end: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
}

:host(.item-radio) ::slotted(ion-label),
:host(.item-toggle) ::slotted(ion-label) {
  -webkit-margin-start: 0px;
  margin-inline-start: 0px;
}

::slotted(ion-label) {
  -webkit-margin-start: 0;
  margin-inline-start: 0;
  -webkit-margin-end: 8px;
  margin-inline-end: 8px;
  margin-top: 10px;
  margin-bottom: 10px;
}

:host(.item-label-floating),
:host(.item-label-stacked) {
  --min-height: 68px;
}

:host(.item-label-stacked) ::slotted(ion-select.legacy-select),
:host(.item-label-floating) ::slotted(ion-select.legacy-select) {
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 0px;
}

:host(.item-label-fixed) ::slotted(ion-select.legacy-select),
:host(.item-label-fixed) ::slotted(ion-datetime) {
  --padding-start: 0;
}